import type { UpdateUserRolesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateUserRolesDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

type UpdateUserRolesInput = {
  userId: string;
  roleIds: string[];
};

export const useUpdateUserRoles = createMutationHook({
  name: 'updateUserRoles',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.user.updateRoles.mutationOptions({ onSuccess }),
  mapInput: (input: UpdateUserRolesInput) => input,
  // Invalidate users list and per-user read paths so the modal and settings
  // table reflect the updated role membership.
  invalidate: (trpc) => [
    trpc.frontend.user.users.queryKey(),
    trpc.frontend.user.userById.queryKey(),
    trpc.frontend.user.userByIdWithRoles.queryKey(),
    // COI-889: role and module grants feed `getTypePermissions` and
    // `checkNavigationVisibility`, both cached for 60 min with
    // `refetchOnMount: false`.
    trpc.frontend.permission.pathKey(),
  ],
  mapResponse: (result: {
    roles: { id: string; name: string; description: string }[];
  }): UpdateUserRolesMutation => ({
    update_user_roles: {
      roles: result.roles,
    },
  }),
  graphqlDocument: UpdateUserRolesDocument,
  graphqlVariables: (input: UpdateUserRolesInput) => ({ ...input }),
  // user_role/role_type aren't exposed as Hasura root query fields, so they
  // aren't in CacheFieldName — evict the user envelopes (which carry the
  // userRoles relation in their queries) and let refetchQueries refresh the
  // by-id view.
  evictFields: ['auth_user', 'auth_user_by_pk'],
  refetchQueries: [namedOperations.Query.GetAuthUserByIdWithRoles],
});
